import React, { PropsWithChildren } from 'react'
import Markdown from 'markdown-to-jsx'
import styled from 'styled-components'
import { MarkdownPreviewProps } from './markdown-preview.types'
import { Typography } from '../typography'
import { TypographyVariant } from '../typography/typography'
import { ExternalLink } from '../external-link'
import { Theme } from '../../theme'
import { FontSize } from '../../theme/theme'

const listStyles = ({ theme }: { theme: Theme }) => `
  margin: 0;
  padding-left: ${theme.sizes[5]};
  color: inherit;
  line-height: inherit;

    p {
      color: inherit;
      line-height: inherit;
    }
`

const mediaStyles = ({ theme }: { theme: Theme }) => `
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  border: 1px solid ${theme.semantic.borderSubtle};
  border-radius: ${theme.radii.container};
  background: ${theme.semantic.backgroundPrimaryInvert};
`

const StyledMarkdownPreview = styled.div<{ $fontSize: FontSize }>`
  ${({ theme, $fontSize }: { theme: Theme; $fontSize: FontSize }) => `
    width: 100%;

    a {
      display: inline;
      font-size: ${theme.fontSizes[$fontSize]};
    }

    [data-md-paragraph] {
      margin: 0;
      color: ${theme.colors.gray900};
      font-size: ${theme.fontSizes[$fontSize]};
      line-height: ${theme.sizes[5]};
      font-weight: ${theme.fontWeights.regular};
    }

    [data-md-paragraph] + [data-md-paragraph] {
      margin-top: ${theme.fontSizes[$fontSize]};
    }

    * + [data-md-media] {
      margin-top: ${theme.spacing.m};
    }

    ${$fontSize && `font-size: ${theme.fontSizes[$fontSize]};`}
  `}
`

const StyledList = styled.ul`
  ${listStyles}
`

const StyledOrderedList = styled.ol`
  ${listStyles}
`

const StyledVideo = styled.video`
  ${mediaStyles}
`

const StyledEmbed = styled.iframe`
  ${mediaStyles}
  aspect-ratio: 16 / 9;
`

// Raw HTML that must never render in user-authored markdown.
const BLOCKED_TAGS = [
  'script',
  'style',
  'link',
  'meta',
  'base',
  'object',
  'embed',
  'form',
] as const

// Only known video providers may be framed — an arbitrary iframe is a
// phishing/clickjacking surface inside our dashboards.
const ALLOWED_EMBED_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'vimeo.com',
  'player.vimeo.com',
  'loom.com',
  'www.loom.com',
]

const isAllowedEmbedUrl = (url: string) => {
  try {
    return ALLOWED_EMBED_HOSTS.includes(new URL(url).hostname)
  } catch {
    return false
  }
}

// Mirrors markdown-to-jsx's built-in URL sanitizer (not exported from its CJS
// build): decode and strip padding so obfuscated schemes can't slip through.
const isUnsafeUrl = (value: string) => {
  try {
    return /^\s*(javascript|vbscript|data(?!:image)):/i.test(
      decodeURIComponent(value).replace(/[^A-Za-z0-9/:]/g, '')
    )
  } catch {
    return true
  }
}

const sanitizeUrl = (value: string, tag: string, attribute: string) => {
  if (isUnsafeUrl(value)) {
    return null
  }

  if (tag === 'iframe' && attribute === 'src') {
    return isAllowedEmbedUrl(value) ? value : null
  }

  return value
}

const getOverrides = () => {
  const createTypographyOverride = (variant: TypographyVariant) => {
    return ({ children }: PropsWithChildren) => (
      <Typography variant={variant} children={children} />
    )
  }

  const blockedOverrides = Object.fromEntries(
    BLOCKED_TAGS.map((tag) => [tag, () => null])
  )

  return {
    ...blockedOverrides,
    h1: createTypographyOverride('h1'),
    h2: createTypographyOverride('h2'),
    h3: createTypographyOverride('h3'),
    h4: createTypographyOverride('h4'),
    h5: createTypographyOverride('h5'),
    h6: createTypographyOverride('h6'),
    // Render paragraphs as <div> so block-level raw HTML in the markdown can't
    // nest invalidly inside a <p> (which desyncs React and crashes on re-render).
    p: ({ children }: PropsWithChildren) => (
      <div data-md-paragraph="">{children}</div>
    ),
    a: ({ children, href }: { children: string; href: string }) => (
      <ExternalLink href={href} text={children} hideIcon />
    ),
    ul: ({ children }: PropsWithChildren) => (
      <StyledList data-testid="pointed-list">{children}</StyledList>
    ),
    ol: ({ children }: PropsWithChildren) => (
      <StyledOrderedList data-testid="ordered-list">
        {children}
      </StyledOrderedList>
    ),
    video: ({
      children,
      ...props
    }: PropsWithChildren<React.VideoHTMLAttributes<HTMLVideoElement>>) => (
      <StyledVideo controls data-md-media="" {...props}>
        {children}
      </StyledVideo>
    ),
    // A src-less iframe means the sanitizer rejected the URL — render nothing.
    iframe: (props: React.IframeHTMLAttributes<HTMLIFrameElement>) =>
      props.src ? (
        <StyledEmbed
          title="Embedded video"
          allowFullScreen
          data-md-media=""
          {...props}
        />
      ) : null,
  }
}

export const MarkdownPreview = ({
  content,
  className,
  testId,
  fontSize = 'body',
}: MarkdownPreviewProps) => (
  <StyledMarkdownPreview
    className={className}
    $fontSize={fontSize}
    data-testid={testId}
  >
    <Markdown
      options={{
        overrides: getOverrides(),
        forceBlock: true,
        sanitizer: sanitizeUrl,
        tagfilter: false,
      }}
    >
      {content}
    </Markdown>
  </StyledMarkdownPreview>
)
